import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Analytics } from "@vercel/analytics/react"
import { BrowserRouter as Router, Routes, Route, Link, useLocation} from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import CustomCursor from './components/CustomCursor';
import DarkModeToggle from './components/DarkMode';
import Home from './components/Home';
import Footer from './components/Footer';
import './App.css';

const Blog = lazy(() => import('./components/Blogs'));
const Projects = lazy(() => import('./components/Projects'));
const NotFound = lazy(() => import('./components/NotFound'));

const Header = ({ isDarkMode, toggleDarkMode }) => {
  const location = useLocation();
  
  return (
    <header>
      <div className="nav-controls">
        <div className="mode-toggle">
          <Link to={location.pathname === '/projects' ? '/' : '/projects'} className="nav-link">
            {location.pathname === '/projects' ? 'Home' : 'Projects'}
          </Link>
          <Link to={location.pathname === '/blogs' ? '/' : '/blogs'} className="nav-link">
            {location.pathname === '/blogs' ? 'Home' : 'Blog'}
          </Link>
          <img 
            src={isDarkMode ? '/favicon-dark.ico' : '/favicon-light.ico'} 
            alt={isDarkMode ? 'Dark Mode' : 'Light Mode'} 
            className="mode-icon"
          />
          <DarkModeToggle isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
        </div>
      </div>
    </header>
  );
};

const AppContent = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const location = useLocation();

  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode);
    updateFavicon(isDarkMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleDarkMode = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
    document.body.classList.toggle('dark-mode', next);
    updateFavicon(next);
  };
  
  const updateFavicon = (dark) => {
    const favicon = document.getElementById('favicon');
    if (favicon) {
      favicon.href = dark ? '/favicon-dark.ico' : '/favicon-light.ico';
    }
  };

  useEffect(() => {
    updateFavicon(isDarkMode);
  }, [isDarkMode]);

  return (
    <div className={`app ${isDarkMode ? 'dark-mode' : ''}`}>
      <CustomCursor isDarkMode={isDarkMode} />
      <Header isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`page-container ${location.pathname === '/' ? 'home-page' : ''}`}>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/blogs/*" element={<Blog />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Footer />
      </div>
    </div>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
        <Analytics />
      </Router>
    </HelmetProvider>
  );
};

export default App;
